// Participantes, por residencial. La API es la ÚNICA fuente: sin respaldos
// inventados (ver la nota de generateSeed abajo).
// Privacidad de menores: por adulto responsable + apartamento, o solo por apartamento.

import { apiFetch } from "./api";

export type UserTipo = "nino" | "joven" | "adulto";
export type RegistroTipo = "nombre" | "apartamento";

export interface AppUser {
  id: string;
  tipo: UserTipo;
  registro: RegistroTipo;
  firstName?: string;
  lastName?: string;
  guardian?: string;
  unidad: string;
  /** Código de apartamento que asigna Vivienda Pública; compartido por los
   *  residentes de un mismo apartamento. Opcional. */
  tCode?: string;
  email?: string;
  grade?: string;
  status: "activo" | "pendiente";
  createdAt: string;
  visits: number;
  /** El residencial al que pertenece. La API siempre lo devuelve; se perdía en
   *  `mapRow`, y por eso el panel no podía dividir los usuarios por residencial. */
  siteCode?: string;
  siteName?: string;
  /** Último ingreso REAL. Distinto de `activated_at` (cuándo se le aprobó el
   *  acceso): una cuenta aprobada que nunca se usó tiene esto en null. */
  lastLoginAt?: string;
}

/*
 * Aquí vivía `generateSeed()`: 30 participantes ficticios con nombres comunes de PR
 * que se enseñaban cuando la API fallaba. ELIMINADO al ir a producción.
 *
 * Como muleta de demo tenía sentido; en vivo era una mentira con consecuencias. Los
 * usuarios se cuentan POR RESIDENCIAL y las licencias se facturan sobre ellos, así
 * que treinta participantes inventados en un centro son treinta licencias que nadie
 * contrató. Un fallo de la API ahora se enseña como fallo.
 */

function mapRow(r: any): AppUser {
  return {
    id: r.id, tipo: r.tipo, registro: r.registro,
    firstName: r.first_name ?? undefined, lastName: r.last_name ?? undefined,
    guardian: r.guardian ?? undefined, unidad: r.unidad, tCode: r.t_code ?? undefined,
    email: r.email ?? undefined, grade: r.grade ?? undefined,
    status: r.status, createdAt: r.created_at, visits: r.visits ?? 0,
    siteCode: r.site_code ?? undefined,
    siteName: r.site_name ?? undefined,
    lastLoginAt: r.last_login_at ?? undefined,
  };
}

/** Filtros de la lista. Todos opcionales; el alcance del token manda igual. */
export interface UserQuery {
  siteCode?: string;
  q?: string;
  tipo?: UserTipo;
  grade?: string;
  status?: "activo" | "pendiente";
  /** Agrupar por apartamento: todos los residentes con este T-Code. */
  tCode?: string;
  limit?: number;
  offset?: number;
}

export interface UserPage {
  users: AppUser[];
  /** Cuántos hay en total con esos filtros — no cuántos vinieron en esta página. */
  total: number;
  offset: number;
  limit: number;
  error: string | null;
}

/**
 * Página de usuarios. `siteCode` acota a un residencial; el alcance del token manda
 * igual (el parámetro solo puede estrechar).
 *
 * SIN respaldo inventado. Antes, cualquier fallo caía a un seed de 30 nombres
 * ficticios. En un piloto era una muleta de demo; con la plataforma en vivo es
 * inaceptable: los usuarios son POR RESIDENCIAL y las licencias se facturan sobre
 * ellos, así que inventar treinta participantes en un centro es inventar una
 * factura. Un fallo se dice; no se rellena.
 */
export async function getUsers(query: UserQuery = {}): Promise<UserPage> {
  const qs = new URLSearchParams();
  if (query.siteCode) qs.set("siteCode", query.siteCode);
  if (query.q?.trim()) qs.set("q", query.q.trim());
  if (query.tipo) qs.set("tipo", query.tipo);
  if (query.grade) qs.set("grade", query.grade);
  if (query.status) qs.set("status", query.status);
  if (query.tCode) qs.set("tCode", query.tCode);
  qs.set("limit", String(query.limit ?? 25));
  qs.set("offset", String(query.offset ?? 0));

  try {
    const res = await apiFetch(`/users?${qs}`);
    if (res.ok) {
      const d = await res.json();
      if (Array.isArray(d.users)) {
        return {
          users: d.users.map(mapRow),
          total: d.total ?? d.users.length,
          offset: d.offset ?? 0,
          limit: d.limit ?? 25,
          error: null,
        };
      }
    }
    const d = await res.json().catch(() => ({}));
    return { ...vacio(), error: d.error ? `${d.error} (HTTP ${res.status})` : `HTTP ${res.status}` };
  } catch (e: any) {
    return { ...vacio(), error: e?.message ?? "sin conexión" };
  }
}

function vacio(): UserPage {
  return { users: [], total: 0, offset: 0, limit: 25, error: null };
}

/** La ficha completa de una persona: quién es y qué ha hecho. */
export interface UserDetail {
  user: {
    id: string; role: string; first_name: string | null; last_name: string | null;
    status: string; created_at: string; activated_at: string | null;
    last_login_at: string | null; tipo: UserTipo; registro: RegistroTipo;
    unidad: string | null; t_code: string | null; visits: number; grade: string | null;
    guardian: string | null; guardian_phone: string | null;
    guardian_consent_at: string | null; mobile_enabled: boolean;
    email: string | null; site_code: string | null; site_name: string | null;
    municipio: string | null; activado_por: string | null;
  };
  /** Salas permitidas del participante (null = todas). */
  allowedRooms: string[] | null;
  /** Otros residentes del mismo apartamento (misma sede + T-Code + unidad). */
  apartmentMates: { id: string; displayName: string }[];
  assignments: { slug: string; title: string; subject: string | null; status: string; assigned_at: string; due_at: string | null; asignado_por: string | null }[];
  sessions: { module_slug: string | null; lesson_id: string | null; started_at: string; duration_s: number | null; source: string; result: string | null }[];
  quizzes: { title: string; score: number | null; taken_at: string }[];
  tickets: { id: string; subject: string; status: string; created_at: string }[];
  /**
   * Quién le hizo qué y cuándo. Puede llegar vacío —o no llegar— si la Lambda va
   * por delante de la migración; la ficha no depende de esto para pintarse.
   */
  auditoria?: EntradaAuditoria[];
}

/** Una línea del historial de cambios de una persona. */
export interface EntradaAuditoria {
  at: string;
  actor_usuario: string | null;
  actor_role: string | null;
  accion: string;
  detalle: Record<string, unknown> | null;
}

/**
 * Cómo se lee cada acción. En pasado y con sujeto implícito: la línea completa
 * queda "Aprobó el acceso · bib@… · 19 ago 2026".
 *
 * Una acción desconocida se enseña tal cual en vez de esconderse: un historial
 * que se calla lo que no reconoce es peor que uno feo.
 */
export const ACCION_LABEL: Record<string, string> = {
  "usuario.crear": "Creó el registro",
  "usuario.aprobar": "Aprobó el acceso",
  "usuario.suspender": "Suspendió el acceso",
  "usuario.consentimiento": "Registró el consentimiento del tutor",
  "usuario.borrar": "Borró el registro",
  "usuario.nombre": "Corrigió el nombre",
  "usuario.modulos": "Cambió los módulos",
  "usuario.password": "Restableció la contraseña",
  "staff.perfil": "Corrigió el nombre o el correo",
  "staff.rol": "Cambió el rol o el alcance",
  "staff.estado": "Suspendió o reactivó la cuenta",
  "staff.password": "Restableció la contraseña",
  "staff.borrar": "Borró la cuenta",
  "staff.compania": "Cambió la compañía",
};

/** Conflicto de T-Code que devuelve el backend (409 estructurado). */
export interface TCodeConflict {
  reason: string; // "diverges-from-unit" | "unit-has-many" | "code-in-other-unit"
  offending: string[];
}

/** Identidad del propio participante, para "Mi perfil" (GET /me). */
export interface MeProfile {
  firstName: string | null;
  lastName: string | null;
  registro: RegistroTipo;
  unidad: string | null;
  tCode: string | null;
  tipo: UserTipo;
  grade: string | null;
  siteName: string | null;
  allowedRooms: string[] | null; // null = todas las salas
}

/** La ficha del propio participante autenticado. Solo la suya (id del token). */
export async function getMe(): Promise<{ profile: MeProfile | null; error: string | null }> {
  try {
    const res = await apiFetch("/me");
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { profile: null, error: d.error ?? `HTTP ${res.status}` };
    }
    return { profile: (await res.json()) as MeProfile, error: null };
  } catch (e: any) {
    return { profile: null, error: e?.message ?? "sin conexión" };
  }
}

/**
 * Sella el último acceso tras un ingreso real. Se dispara y se olvida: si falla,
 * la persona ya entró y no se le va a estorbar por una métrica.
 *
 * El servidor sella el id del TOKEN, no uno que mande el cliente — nadie puede
 * marcar el acceso de otro.
 */
export function stampLogin(): void {
  void apiFetch("/auth/login", { method: "POST" }).catch(() => {
    /* sin red: se perderá este sello, no la sesión */
  });
}

export async function getUser(id: string): Promise<{ detail: UserDetail | null; error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}`);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { detail: null, error: d.error ?? `HTTP ${res.status}` };
    }
    return { detail: (await res.json()) as UserDetail, error: null };
  } catch (e: any) {
    return { detail: null, error: e?.message ?? "sin conexión" };
  }
}

/** Fija las salas permitidas de un participante. `allowedRooms` null/[] = todas. */
export async function setUserRooms(id: string, allowedRooms: string[] | null): Promise<{ error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}/rooms`, {
      method: "PATCH",
      body: JSON.stringify({ allowedRooms }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { error: d.error ?? `HTTP ${res.status}` };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e?.message ?? "sin conexión" };
  }
}

export interface NewUser {
  tipo: UserTipo;
  registro: RegistroTipo;
  firstName?: string;
  lastName?: string;
  guardian?: string;
  guardianPhone?: string;
  /** COPPA: el tutor autorizó la participación del menor. Sin esto, el backend
   *  crea la cuenta 'pendiente' (no la activa). Irrelevante para adultos. */
  guardianConsent?: boolean;
  unidad: string;
  /** Código de apartamento (Vivienda Pública). Opcional. */
  tCode?: string;
  email?: string;
  /** Requerido para niños/jóvenes. En adultos el backend usa "adulto". */
  grade?: string;
  /** Solo lo manda quien administra varios residenciales. Una bibliotecaria lo
   *  omite: el servidor usa la sede de su token y no aceptaría otra. */
  siteCode?: string;
}

/**
 * Alta de un participante.
 *
 * Antes mandaba `siteCode: SITE.siteCode` — la constante `"QUINTANA-SJ"`, que no
 * existe en `sites`. El backend contestaba `400 Sede desconocida`, el `catch` se
 * comía el error y el panel recargaba la lista del seed local: parecía que el
 * registro había funcionado y no se había guardado nada. Ahora el error se
 * devuelve para que la UI lo pueda enseñar.
 */
export async function addUser(
  input: NewUser,
): Promise<{
  ok: boolean;
  delivery?: "email";
  /** false = se intentó crear el acceso y FALLÓ. */
  cognito?: boolean;
  cognitoError?: string;
  /** true = no se creó acceso a propósito: la cuenta quedó pendiente (COPPA). */
  accesoPendiente?: boolean;
  conflict?: TCodeConflict;
  error?: string;
}> {
  try {
    const res = await apiFetch("/users", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...input, status: "activo" }),
    });
    // Conflicto de T-Code: estructurado, para que la UI ramifique por `reason` y no
    // por texto. El alta NUNCA aplica cambios masivos: se corrige desde la ficha.
    if (res.status === 409) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, conflict: { reason: d.reason ?? "conflict", offending: d.offending ?? [] } };
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, error: d.error ?? `HTTP ${res.status}` };
    }
    const d = await res.json().catch(() => ({}));
    return {
      ok: true, delivery: d.delivery, cognito: d.cognito,
      cognitoError: d.cognitoError, accesoPendiente: d.accesoPendiente,
    };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "sin conexión" };
  }
}

// --- Alta de usuarios por rol (super_admin/admin). El backend provisiona la cuenta
//     Cognito post-flip y Cognito entrega la invitación directamente por correo. ---
export interface CreateUserInput {
  role: "super_admin" | "admin" | "agent_admin" | "librarian" | "participant";
  firstName: string;
  lastName?: string;
  email: string;
  siteCode?: string;
  tipo?: UserTipo;
  grade?: string;
  unidad?: string;
  /** Menores registrados por nombre. Sin tutor Y autorización la cuenta sale
   *  'pendiente', y un pendiente NO recibe acceso (protección COPPA). */
  guardian?: string;
  guardianPhone?: string;
  guardianConsent?: boolean;
  /** Solo para `agent_admin`: la compañía administradora. Sus residenciales salen
   *  de ahí — no se asignan uno por uno. */
  agentCompanyId?: string;
}
export interface CreateUserResult {
  ok: boolean;
  cognito?: boolean;
  delivery?: "email";
  error?: string;
}
export async function createUser(input: CreateUserInput): Promise<CreateUserResult> {
  try {
    const res = await apiFetch("/users", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: d.error ?? `HTTP ${res.status}` };
    return { ok: true, cognito: d.cognito, delivery: d.delivery };
  } catch (e: any) {
    return { ok: false, error: e.message ?? "sin red" };
  }
}

/**
 * Restablece la contraseña de un participante: le llega un CÓDIGO al correo.
 *
 * Nadie ve ni dicta la contraseña — la persona elige la suya con ese código. Si
 * todavía no ha entrado nunca, el servidor responde 409 y lo que hace falta es
 * «Reenviar invitación».
 */
export async function resetUserPassword(id: string): Promise<{ error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}/password`, { method: "POST" });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { error: d.error ?? `HTTP ${res.status}` };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e?.message ?? "sin conexión" };
  }
}

/** Corrige el nombre de un participante. No aplica al registro por apartamento. */
export async function updateUserName(
  id: string,
  firstName: string,
  lastName: string,
): Promise<{ error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}/nombre`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ firstName, lastName }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { error: d.error ?? `HTTP ${res.status}` };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e?.message ?? "sin conexión" };
  }
}

/**
 * Borra el registro de un participante — y le quita el acceso, que es la parte
 * que faltaba: antes se borraba la ficha y la cuenta de Cognito seguía viva.
 *
 * Con historial el servidor responde 409 y NO borra nada: ahí el camino es
 * suspender. El mensaje del servidor lo explica y se enseña tal cual.
 */
export async function deleteUser(id: string): Promise<{ error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { error: d.error ?? `HTTP ${res.status}` };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e?.message ?? "sin conexión" };
  }
}

/** Reenvía el correo de invitación a un participante. */
export async function resendUserInvitation(id: string): Promise<{ error: string | null }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}/invitacion`, { method: "POST" });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { error: d.error ?? `HTTP ${res.status}` };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e?.message ?? "sin conexión" };
  }
}

export interface ResultadoEstado {
  ok: boolean;
  status?: string;
  acceso?: {
    /** Lo que se pretendía: sobrevive aunque no se pudiera consultar Cognito. */
    intencion: "abrir" | "cerrar" | "ninguna";
    accion: "ninguna" | "crear" | "habilitar" | "deshabilitar";
    ok: boolean;
    error?: string;
  };
  error?: string;
}

/**
 * Cambia el estado de un participante. Devuelve QUÉ PASÓ: antes era `void` y se
 * tragaba los errores, así que una aprobación que no llegaba a crear el acceso
 * era indistinguible de una que sí.
 */
export async function setUserStatus(
  id: string,
  status: "activo" | "pendiente",
  opciones: { guardianConsent?: boolean } = {},
): Promise<ResultadoEstado> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ status, ...opciones }),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: d.error ?? `HTTP ${res.status}` };
    return { ok: true, status: d.user?.status, acceso: d.acceso };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "sin conexión" };
  }
}

/**
 * Pone la cuenta de acceso al día sin tocar el estado ni la auditoría.
 *
 * Es lo que usa «Reintentar»: reenviar `status: "activo"` por el PATCH borraría
 * la fecha real de aprobación y pondría la de hoy con otro nombre.
 *
 * Con `guardianConsent: true` registra además un consentimiento que faltaba —el
 * único camino para alguien que quedó activo sin él, porque en su ficha solo le
 * sale «Suspender».
 */
export async function sincronizarAcceso(
  id: string,
  opciones: { guardianConsent?: boolean } = {},
): Promise<ResultadoEstado> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}/sincronizar-acceso`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(opciones),
    });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: d.error ?? `HTTP ${res.status}` };
    return { ok: true, acceso: d.acceso };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "sin conexión" };
  }
}

/**
 * Qué contarle a quien acaba de aprobar o suspender. `null` = nada que decir.
 *
 * Se mira la INTENCIÓN, no la acción. Si la consulta a Cognito falló, la acción
 * es "ninguna" —no se llegó a decidir— pero la intención se conserva: sin ella,
 * una suspensión fallida diría "todavía no puede entrar" de alguien que
 * probablemente SÍ sigue pudiendo. Lo contrario de la verdad.
 */
export function avisoDeAcceso(r: ResultadoEstado): string | null {
  if (!r.ok) return `No se pudo: ${r.error}`;
  if (r.acceso && !r.acceso.ok) {
    const detalle = r.acceso.error ?? "";
    if (r.acceso.intencion === "cerrar") {
      return [
        "El estado cambió a pendiente, pero NO se pudo cerrar el acceso.",
        "",
        detalle,
        "",
        "Esta persona PODRÍA SEGUIR ENTRANDO. Usa «Reintentar acceso» en su ficha.",
      ].join("\n");
    }
    return [
      "El estado cambió, pero NO se pudo crear el acceso.",
      "",
      detalle,
      "",
      "Todavía no puede entrar. Usa «Reintentar acceso» en su ficha.",
    ].join("\n");
  }
  if (r.acceso?.accion === "crear") {
    return "Aprobada. Le enviamos al correo la invitación para entrar.";
  }
  if (r.acceso?.accion === "deshabilitar") {
    return [
      "Suspendida. No podrá iniciar una sesión nueva.",
      "",
      // Ocho horas: `idTokenValidity` en infra/lib/ikids-auth-stack.ts.
      "Si tenía una sesión abierta, puede seguir dentro hasta una hora más.",
    ].join("\n");
  }
  return null;
}

/**
 * Edita campos del participante (allow-list: status/tCode + bandera
 * applyToApartment). Devuelve el conflicto estructurado en 409 para que la ficha
 * decida por `reason`: `diverges-from-unit` ofrece aplicar a todo el apartamento;
 * `code-in-other-unit` no (ese código pertenece a otro apto).
 */
export async function updateUserFields(
  id: string,
  fields: { status?: "activo" | "pendiente"; tCode?: string; applyToApartment?: boolean },
): Promise<{ ok: boolean; conflict?: TCodeConflict; error?: string }> {
  try {
    const res = await apiFetch(`/users/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(fields),
    });
    if (res.status === 409) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, conflict: { reason: d.reason ?? "conflict", offending: d.offending ?? [] } };
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      return { ok: false, error: d.error ?? `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (e: any) {
    return { ok: false, error: e?.message ?? "sin conexión" };
  }
}

export function displayName(u: AppUser): string {
  if (u.tipo === "adulto") return `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  if (u.registro === "apartamento") return u.unidad;
  return u.firstName ? `${u.firstName} (${u.unidad})` : u.unidad;
}

/*
 * Aquí vivían SEED_SUBJECT_USAGE, SEED_QUIZ y SEED_VISITS_WEEK: «datos agregados
 * dummy para el dashboard». No eran una maqueta olvidada en una rama — estaban
 * EN PRODUCCIÓN, y tres de las cuatro cifras grandes del panel salían de ellas:
 * 412 quizzes completados, 84 % de promedio y la gráfica de materias más usadas.
 * Fijas, iguales en los 122 residenciales, y un admin las leía como reales.
 *
 * Se van porque ya hay de dónde sacar las de verdad: `GET /analytics/resumen`
 * agrega `usage_sessions`, la tabla que LessonPlayer y Juegos llevaban meses
 * llenando sin que nadie la leyera. Ver web/src/lib/analitica.ts.
 *
 * SEED_VISITS_WEEK ni siquiera se usaba en ningún sitio.
 */
